import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React, {useRef} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Pill from "@khanacademy/wonder-blocks-pill";
import {
    border,
    semanticColor,
    sizing,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {mediaQueries} from "@khan/shared-styles";

import type {ToolCategories} from "./types.ts";
import {i18nToolCategory, toCEDARToolCategory} from "./utils/categories.ts";

interface Props {
    selected: ToolCategories;
    setSelected: (selected: ToolCategories) => void;
    categoriesToRender: ToolCategories[];
}

/**
 * Component to render the tool category filters. These look like pills and
 * behave like radio buttons: only one can be selected at a time. They are used
 * to temporarily filter the list of tool cards shown in the all-tools view.
 */
export const ToolCategoryFilters = ({
    selected,
    setSelected,
    categoriesToRender,
}: Props): React.ReactElement => {
    const categoriesGroupRef = useRef<HTMLDivElement>(null);

    const handlePillSelect = (pillIndex: number) => {
        const toolCategory = categoriesToRender[pillIndex];

        publishAnalyticsEventForMinorConversion({
            eventType: "AiTeacherToolsCategorySelected",
            eventSchemaVersion: 3,
            toolCategory: toCEDARToolCategory(toolCategory),
        });

        setSelected(toolCategory);

        // When a pill is selected, focus on it so that keyboard navigation
        // works as expected:
        const pillElement = categoriesGroupRef.current?.children[pillIndex];
        if (pillElement instanceof HTMLButtonElement) {
            pillElement.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const selectedIndex = categoriesToRender.indexOf(selected);

        switch (e.key) {
            case "ArrowRight":
            case "ArrowDown":
                const nextIndex =
                    (selectedIndex + 1) % categoriesToRender.length;
                handlePillSelect(nextIndex);
                break;
            case "ArrowLeft":
            case "ArrowUp":
                const prevIndex =
                    (selectedIndex - 1 + categoriesToRender.length) %
                    categoriesToRender.length;
                handlePillSelect(prevIndex);
                break;
            default:
                break;
        }
    };

    return (
        <View
            style={styles.categoriesToRender}
            role="radiogroup"
            ref={categoriesGroupRef}
            aria-label={t`Filter tool categories`}
            onKeyDown={handleKeyDown}
        >
            {categoriesToRender.map((toolCategory, i) => {
                const checked = toolCategory === selected;
                const kind = checked ? "neutral" : "transparent";

                return (
                    <Pill
                        key={toolCategory}
                        size="large"
                        kind={kind}
                        onClick={() => handlePillSelect(i)}
                        style={[styles.pill, checked && styles.pillChecked]}
                        role="radio"
                        aria-checked={checked}
                        // NOTE(reid): Like with radio buttons, only the
                        // selected filter is listed in the tab order. Arrow
                        // keys can be used to navigate between the filters.
                        tabIndex={checked ? 0 : -1}
                    >
                        <View style={styles.pillContent}>
                            {i18nToolCategory(toolCategory)}
                        </View>
                    </Pill>
                );
            })}
        </View>
    );
};

const styles = StyleSheet.create({
    categoriesToRender: {
        flexDirection: "row",
        width: "100%",
        overflowY: "visible",
        overflowX: "scroll",
        gap: sizing.size_080,
        padding: sizing.size_040,
    },
    pill: {
        flexShrink: 0,
        height: sizing.size_400,
        paddingInline: sizing.size_160,
        outline: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
        [mediaQueries.xsOrSmaller]: {
            height: sizing.size_320,
            marginBlockStart: spacing.xxxSmall_4,
            marginBlockEnd: spacing.xxxSmall_4,
        },
    },
    pillChecked: {
        color: semanticColor.core.foreground.instructive.default,
        outline: `${border.width.thin} solid ${semanticColor.core.border.instructive.default}`,
    },
    pillContent: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0,
    },
});
